import { gql } from '@apollo/client';
import { resolveInvestmentActivity } from '@orn/global/investments/investmentActivity.js';
import CollapseButton from '@orn/shared-web/components/Button/CollapseButton';
import LoadingIndicator from '@orn/shared-web/components/LoadingIndicator';
import Table, { TableCard } from '@orn/shared-web/components/Table';
import { SwitchHorizontalIcon } from '@orn/shared-web/icons';
import { cx } from '@orn/shared-web/utils/cx';
import formatQuantity from '@orn/shared-web/utils/formatQuantity';
import * as React from 'react';

import { useContainerBreakpoint } from '../../../hooks/useContainerBreakpoint';
import { useContainerWidth } from '../../../hooks/useContainerWidth';
import { useInfiniteScroll } from '../../../hooks/useInfiniteScroll';
import { formatCalendarDate } from '../../../utils/formatting';
import {
  investmentAmountDisplay,
  investmentMoneyText,
  investmentTransactionLabel,
} from '../../../utils/investments/investmentTransactionDisplay';
import Entity from '../../entity';
import { TransactionPairTooltip } from '../elements';
import { InvestmentActivityFilterDropdown } from '../filters';
import { EmptyTableState, ErrorTableState, LoadingTableState, type IEmptyTableStateProps } from '../helpers';

import type { InvestmentTransactionTableRowFragment } from './__generated__/BaseInvestmentTransactionsTable.gql';
import type { InvestmentActivity } from '../../../__generated__/globalTypes';

const EMPTY_CELL = '—';
/** Narrower than this, the header cannot fit the title beside the activity filter. */
const HEADER_FILTER_MIN_WIDTH_PX = 480;

interface IBaseInvestmentTransactionsTableProps {
  rows: InvestmentTransactionTableRowFragment[];
  /** The selected date range, as the pane names it. */
  subtitle?: React.ReactNode;
  isLoading?: boolean;
  isLoadingMore?: boolean;
  hasError?: boolean;
  hasNextPage?: boolean;
  onLoadMore?: () => void;
  /** A note after the last row, shown once no page is left to load. */
  footer?: React.ReactNode;
  /** With no rows, `EmptyTableState` from these; without them, the account has no stored activity. */
  emptyStateProps?: IEmptyTableStateProps;
  /** The activity filter's value; null shows all activity. */
  selectedActivity?: InvestmentActivity | null;
  /** With this, the header carries the activity filter. */
  onActivityChange?: (activity: InvestmentActivity | null) => void;
}

const InvestmentTransactionTableRowFragmentDocument = gql`
  fragment InvestmentTransactionTableRow on InvestmentTransaction {
    id
    date
    name
    type
    subtype
    quantity
    price
    amount
    currencyCode
    unofficialCurrencyCode
    security {
      id
      tickerSymbol
      name
    }
    pairedTransaction {
      id
      date
      amount
      currencyCode
      financialAccount {
        id
        subtype
        mask
        name
        ...EntityAvatar
      }
    }
  }
  ${Entity.Avatar.Fragment}
`;

/**
 * An investment account's transactions in the order given: the date, what happened and in
 * which security, the position's quantity and price, and the cash amount. A cash movement paired
 * with a bank transaction carries ⇄, whose tooltip names the bank side. Pure: rows, paging,
 * the footer note, and the activity filter's value arrive as props, and rows open nothing.
 * Lays out by its own width, not the viewport's: below `sm` the date moves under the activity,
 * Quantity and Price show from `lg`, and a table narrower than 480px moves the activity filter
 * from the header to a row under it.
 */
const BaseInvestmentTransactionsTable = ({
  rows,
  subtitle,
  isLoading = false,
  isLoadingMore = false,
  hasError = false,
  hasNextPage = false,
  onLoadMore,
  footer,
  emptyStateProps,
  selectedActivity = null,
  onActivityChange,
}: IBaseInvestmentTransactionsTableProps) => {
  const [isCollapsed, setIsCollapsed] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const isSmOrUp = useContainerBreakpoint(containerRef, 'sm');
  const isLgOrUp = useContainerBreakpoint(containerRef, 'lg');
  const { containerWidth } = useContainerWidth(containerRef);
  const isFilterInHeader = containerWidth >= HEADER_FILTER_MIN_WIDTH_PX;
  const { sentinelRef } = useInfiniteScroll({
    onLoadMore: onLoadMore ?? (() => undefined),
    hasMore: hasNextPage,
    isLoading: isLoadingMore,
    enabled: !isCollapsed && !isLoading && Boolean(onLoadMore),
    threshold: 1,
  });

  const isReady = !isLoading && !hasError;
  const activityFilter = onActivityChange ? (
    <InvestmentActivityFilterDropdown selectedActivity={selectedActivity} onSelect={onActivityChange} />
  ) : null;

  return (
    <TableCard.Root ref={containerRef} className="w-full min-w-0" size={isLgOrUp ? 'md' : 'sm'}>
      <TableCard.Header
        title="Transactions"
        subtitle={subtitle}
        trailingInline
        contentTrailing={
          <div className="flex min-w-0 items-center justify-end gap-4">
            {isFilterInHeader && activityFilter}
            <div className="shrink-0">
              <CollapseButton isCollapsed={isCollapsed} onToggle={() => setIsCollapsed(!isCollapsed)} />
            </div>
          </div>
        }
      />
      {!isCollapsed && (
        <>
          {!isFilterInHeader && activityFilter && (
            <div className="border-tertiary bg-primary flex items-center border-b px-5 py-3.5">{activityFilter}</div>
          )}
          {isLoading && <LoadingTableState />}
          {!isLoading && hasError && <ErrorTableState />}
          {isReady &&
            rows.length === 0 &&
            (emptyStateProps ? (
              <EmptyTableState {...emptyStateProps} />
            ) : (
              <div className="flex h-56 flex-col items-center justify-center px-6 pb-4 pt-3">
                <div className="text-primary text-center text-lg font-medium">
                  No activity has been reported for this account yet.
                </div>
              </div>
            ))}
          {isReady && rows.length > 0 && (
            <Table aria-label="Investment transactions table" selectionMode="none" className="w-full">
              <Table.Header bordered={isSmOrUp} className={isSmOrUp ? undefined : '[visibility:collapse]'}>
                {isSmOrUp && <Table.Head label="Date" className="pr-4" />}
                <Table.Head label="Activity" className="min-w-0" isRowHeader />
                {isLgOrUp && <Table.Head label="Quantity" className="px-4 *:justify-end" />}
                {isLgOrUp && <Table.Head label="Price" className="px-4 *:justify-end" />}
                <Table.Head label="" />
                <Table.Head label="Amount" className="*:justify-end" />
              </Table.Header>
              <Table.Body dependencies={[isSmOrUp, isLgOrUp]} items={rows}>
                {(row) => {
                  const securityLabel = [row.security?.tickerSymbol, row.security?.name].filter(Boolean).join(' · ');
                  const detail = securityLabel || row.name;
                  // A cash row can carry its amount as a quantity at a price of 1; only a security makes a position.
                  const quantity = row.security ? row.quantity : null;
                  const amount = investmentAmountDisplay({
                    amount: row.amount,
                    activity: resolveInvestmentActivity(row.type, row.subtype),
                    currencyCode: row.currencyCode,
                    unofficialCurrencyCode: row.unofficialCurrencyCode,
                  });

                  return (
                    <Table.Row key={row.id} className="h-16 hover:bg-transparent">
                      {isSmOrUp && (
                        <Table.Cell className="w-0 pr-4">
                          <div className="text-primary whitespace-nowrap text-base font-medium">
                            {formatCalendarDate(row.date)}
                          </div>
                        </Table.Cell>
                      )}
                      {/* TransactionsTable's row heights: 68px, and 76px once the date moves under the name. */}
                      <Table.Cell className={cx('w-full min-w-0 max-w-0 px-4', isSmOrUp ? 'py-3' : 'py-4')}>
                        <div className="flex min-w-0 flex-col">
                          <span className="text-primary truncate text-base font-medium">
                            {investmentTransactionLabel(row.type, row.subtype)}
                          </span>
                          <span className="text-tertiary truncate text-sm font-medium">
                            {isSmOrUp ? detail : `${formatCalendarDate(row.date, 'medium')} · ${detail}`}
                          </span>
                        </div>
                      </Table.Cell>
                      {isLgOrUp && (
                        <Table.Cell className="w-0 px-4">
                          <div className="text-tertiary whitespace-nowrap text-right text-sm font-medium">
                            {/* A sell's direction is in its label, as with a trade's neutral amount. */}
                            {quantity ? formatQuantity(Math.abs(quantity)) : EMPTY_CELL}
                          </div>
                        </Table.Cell>
                      )}
                      {isLgOrUp && (
                        <Table.Cell className="w-0 px-4">
                          <div className="text-tertiary whitespace-nowrap text-right text-sm font-medium">
                            {quantity && row.price ? investmentMoneyText(row.price, row) : EMPTY_CELL}
                          </div>
                        </Table.Cell>
                      )}
                      <Table.Cell className="w-0 px-2">
                        <div className="flex items-center justify-center">
                          {row.pairedTransaction?.financialAccount && (
                            <TransactionPairTooltip
                              pair={{
                                ...row.pairedTransaction,
                                financialAccount: row.pairedTransaction.financialAccount,
                              }}
                            >
                              <SwitchHorizontalIcon className="text-quaternary size-5" />
                            </TransactionPairTooltip>
                          )}
                        </div>
                      </Table.Cell>
                      <Table.Cell className="w-0 pl-4">
                        <div
                          className={cx(
                            'whitespace-nowrap text-right text-base font-medium',
                            amount.tone === 'success' ? 'text-success-primary' : 'text-primary'
                          )}
                        >
                          {amount.text}
                        </div>
                      </Table.Cell>
                    </Table.Row>
                  );
                }}
              </Table.Body>
            </Table>
          )}
          {isReady && hasNextPage && onLoadMore && (
            <div className="bg-secondary_subtle border-secondary flex items-center justify-center gap-3 border-t px-6 py-4">
              <div ref={sentinelRef} className="flex items-center justify-center">
                <LoadingIndicator size="sm" />
              </div>
            </div>
          )}
          {isReady && !hasNextPage && footer && (
            <div className="border-tertiary text-tertiary flex items-center justify-center border-t px-6 pb-4 pt-3 text-center text-sm">
              {footer}
            </div>
          )}
        </>
      )}
    </TableCard.Root>
  );
};

BaseInvestmentTransactionsTable.Fragment = InvestmentTransactionTableRowFragmentDocument;

export default BaseInvestmentTransactionsTable;
